import { useUser } from "@clerk/clerk-react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
	ConnectAccountManagement,
	ConnectBalances,
	ConnectComponentsProvider,
	ConnectDocuments,
	ConnectNotificationBanner,
	ConnectPayments,
} from "@stripe/react-connect-js";
import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../lib/apiClient";

// Define types for better type safety
interface ApiError {
	response?: {
		status: number;
	};
	message?: string;
}

export default function Account() {
	const { user, isLoaded } = useUser();
	const navigate = useNavigate();
	const [stripeConnectInstance, setStripeConnectInstance] = useState<ReturnType<
		typeof loadConnectAndInitialize
	> | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (!isLoaded || !user?.id) {
			return;
		}

		const initializeStripeConnect = async () => {
			try {
				setIsLoading(true);
				setError(null);

				const fetchClientSecret = async (): Promise<string> => {
					try {
						const result = await apiClient().get<{ clientSecret: string }>(
							`/connect/session/dashboard/${user.id}`,
						);
						return result.data.clientSecret;
					} catch (err: unknown) {
						const apiError = err as ApiError;
						if (apiError.response?.status === 500 || apiError.response?.status === 404) {
							navigate("/start");
							throw new Error("Redirecting to onboarding.");
						}
						throw new Error("Failed to initialize payment dashboard. Please try again.");
					}
				};

				if (typeof fetchClientSecret === "undefined") {
					throw new Error("Fetch client secret function is not defined.");
				}

				const instance = loadConnectAndInitialize({
					publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string,
					fetchClientSecret: fetchClientSecret,
				});

				setStripeConnectInstance(instance);
			} catch (err: unknown) {
				const error = err as Error;
				console.error("Stripe Connect initialization error:", error);
				setError(error.message || "Failed to initialize payment dashboard");
			} finally {
				setIsLoading(false);
			}
		};

		initializeStripeConnect();
	}, [user?.id, isLoaded, navigate]);

	return (
		<div className="min-h-screen w-full bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50">
			{/* Main Content */}
			<main className="p-0 md:p-8">
				<div className="max-w-6xl mx-auto">
					{/* Header */}
					<div className="flex flex-col md:flex-row justify-between items-center mb-8">
						<div className="flex items-center gap-4">
							<h1 className="text-2xl font-semibold text-gray-900">Account</h1>
						</div>
					</div>

					{/* Stripe Connect Container */}
					{error ? (
						<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-6">
							<div className="text-center py-12">
								<AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
								<h3 className="text-lg font-semibold text-gray-900 mb-2">
									Unable to Load Account Information
								</h3>
								<p className="text-gray-600 mb-4">{error}</p>
								{error.includes("onboarding") && (
									<button
										type="button"
										onClick={() => navigate("/start")}
										className="bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 text-white font-semibold px-6 py-2 rounded-full transition-all duration-300"
									>
										Start Onboarding
									</button>
								)}
							</div>
						</div>
					) : isLoading ? (
						<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-6">
							<div className="text-center py-12">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4" />
								<p className="text-gray-500">Loading account information...</p>
							</div>
						</div>
					) : stripeConnectInstance && user ? (
						<ConnectComponentsProvider connectInstance={stripeConnectInstance}>
							<div className="space-y-6">
								{/* Notification Banner */}
								<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-6">
									<ConnectNotificationBanner />
								</div>

								{/* Payments */}
								<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-6">
									<ConnectBalances />
								</div>

								{/* Balances */}
								<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-6">
									<ConnectPayments />
								</div>

								{/* Account Management */}
								<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-6">
									<ConnectAccountManagement />
								</div>

								{/* Account Documents */}
								<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-6">
									<ConnectDocuments />
								</div>
							</div>
						</ConnectComponentsProvider>
					) : (
						<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-6">
							<div className="text-center py-12">
								<p className="text-gray-500">Please sign in to access your account</p>
							</div>
						</div>
					)}
				</div>
			</main>
		</div>
	);
}
